/**
 * iCal feed entity schemas — the owned contract for the ICAL_FEED page
 * template: one school-published .ics link rendered as a page of its own,
 * through the same calendar screen the main school calendar uses.
 *
 * SHAPE. The web-feed ADDRESSING plus display settings of its own: it is built
 * from the same `WebFeedAddressShape`, so the two surfaces cannot drift on what
 * a legal feed url or feed id is, while the settings differ because the surfaces
 * differ. A web feed asks where the image sits and where a tap lands; a calendar
 * has neither question — a row opens the event detail sheet, always — and asks
 * instead which of the calendar's own chrome the page keeps (SCH-1331).
 * The shared posture (https-only, no domain allowlist, SSRF guard at fetch time)
 * comes with the addressing rather than being restated here. The one thing this
 * surface adds to it is a `webcal://` rewrite at the input edge (SCH-1506) —
 * see `IcalFeedUrlSchema`, which overrides the address shape's `feedUrl` rather
 * than loosening it for the RSS surface that shares it.
 *
 * ADDRESSING. Like a web feed, the configured feed carries an opaque `feedId`
 * minted at authoring time; the client sends slug + `feedId` and the server
 * resolves the url from the page's PUBLISHED version. The CONTRACT the read and
 * write paths must enforce is that the url never travels toward a viewer —
 * Google Calendar and Outlook "secret address" links are bearer capabilities, so
 * publishing one to the client would hand every viewer read access to the
 * calendar at its publisher. This shape only holds the url; the projection that
 * strips it out of the viewer payload, and the re-minting of the feed id when
 * the url changes (so a viewer's cached lane for the old feed can never be
 * served against the new one), land with the read and write paths.
 *
 * The ICAL_FEED enum value is promoted in PlanetScale separately from this
 * code, BEFORE anything that writes the template deploys.
 */

import { z } from 'zod'

import { normalizeCalendarFeedUrl } from '../../security/safe-url'
import { ScreenHeaderConfigSchema, type ScreenHeaderConfig } from './navigation'
import { WebFeedAddressShape, WebFeedUrlSchema } from './web-feed'

// ============================================
// Feed URL — https, with webcal:// accepted at the edge
// ============================================

/**
 * A calendar feed url as an AUTHOR PASTES IT (SCH-1506).
 *
 * Same rule as `WebFeedUrlSchema` — https only, no domain allowlist, SSRF
 * guard at fetch time — with one addition ahead of the check: a `webcal://` or
 * `webcals://` address is rewritten to its `https://` twin first. That is the
 * scheme Google Calendar, Outlook and most school CMSes hand an admin when they
 * click "subscribe", and it names the same document; see
 * `normalizeCalendarFeedUrl` for why the rewrite is safe rather than lenient.
 *
 * DELIBERATELY NOT ON THE BASE SCHEMA. `WebFeedUrlSchema` also types a web-feed
 * item's `link` and `imageUrl`, which are publisher-authored urls rather than
 * pasted addresses; rewriting a scheme there would invent an address nobody
 * chose. So the calendar surface layers this on top instead of loosening the
 * shared rule.
 *
 * `http://` is still refused, with the base schema's own message.
 */
export const IcalFeedUrlSchema = z.preprocess(
  value => (typeof value === 'string' ? normalizeCalendarFeedUrl(value) : value),
  WebFeedUrlSchema
)

/**
 * The same url as an AUTHOR-IN-PROGRESS value. The empty case is load-bearing
 * for exactly the reason `WebFeedUrlDraftSchema` documents: an ICAL_FEED page
 * exists before its address does and the editor autosaves the whole payload, so
 * a shape that refused an empty url would take every autosave down with it
 * until a valid address was typed.
 */
export const IcalFeedUrlDraftSchema = z.union([z.literal(''), IcalFeedUrlSchema])

// ============================================
// Display settings — the calendar's own chrome
// ============================================

/**
 * Which parts of the calendar screen an iCal page keeps (SCH-1331).
 *
 * A page pointing at one athletics or PTA feed is often just "what is coming
 * up", where the month strip and the category filter cost space without earning
 * it. These say so per page. They govern the PAGE LANE only — the main school
 * calendar tab has no page content and is not reachable from here.
 *
 * BOTH DEFAULT TRUE, and the default is load-bearing in three places at once: a
 * page authored before this shipped carries neither key, a viewer on an app
 * bundle older than the renderer change reads neither prop, and an author who
 * never opens the switches gets what they already had. Every one of those paths
 * must land on today's behavior, so the safe value is "show" and the flags are
 * only ever subtractive.
 *
 * Kept as a flat field bag merged into the content shape, mirroring
 * `WebFeedSettingsShape`, so a setting is a key on the page content rather than
 * a nested object the editor has to construct before it can write one switch.
 *
 * `showFilters` hides the filter CONTROL; it does not clear a filter a viewer
 * already applied on that lane, and it is not what decides whether the control
 * is useful — with zero categories the control is already disabled on its own.
 *
 * `header` (SCH-1509) is the third switch and the one shaped differently: it
 * reuses `ScreenHeaderConfigSchema` — the object the tile surface already
 * authors — rather than inventing a second spelling of the same bar, so the two
 * surfaces cannot drift about what "the glass header" is. That is why this one
 * field is a nested object in a bag of flat booleans, and why it carries no zod
 * `.default()`: the shape is shared, but ABSENCE means the opposite thing here
 * than it does on a tile screen, so the answer cannot live on the object. It
 * lives in `ICAL_FEED_SHOW_HEADER_DEFAULT` / `resolveIcalFeedHeaderEnabled`
 * below, beside the two booleans' defaults, which is the single place every
 * consumer reads it from.
 *
 * BELONGING TO THIS SHAPE IS LOAD-BEARING, not tidiness: the backend's viewer
 * projection (`projectIcalFeedContentForViewer`) forwards the settings by
 * parsing them through `IcalFeedSettingsSchema`, which strips every key it does
 * not own. A header switch defined outside this bag would be silently dropped
 * on the way to the app and the switch would do nothing.
 */
export const ICAL_FEED_SHOW_MONTH_STRIP_DEFAULT = true
export const ICAL_FEED_SHOW_FILTERS_DEFAULT = true

/**
 * What an ABSENT `header` key means on this surface: SHOW the bar.
 *
 * INVERTED from the tile surface on purpose (see `ScreenHeaderConfigSchema`).
 * Every ICAL_FEED page in the fleet today renders the glass header and none of
 * them carries this key, so "absent ⇒ off" would take the bar — and with it the
 * hamburger — off live pages the next time the app updated. Same "undefined
 * means show" direction as the two booleans above, and subtractive for the same
 * reason.
 */
export const ICAL_FEED_SHOW_HEADER_DEFAULT = true

/**
 * The one place that turns a stored (or absent, or half-written) `header` into
 * the boolean every consumer wants. Editor, page renderer and any later reader
 * go through this rather than restating the default, so none of them can drift
 * from the schema about what an unset switch means.
 */
export function resolveIcalFeedHeaderEnabled(header: ScreenHeaderConfig | undefined): boolean {
  return header?.enabled ?? ICAL_FEED_SHOW_HEADER_DEFAULT
}

export const IcalFeedSettingsShape = {
  /** The expandable week/month strip above the agenda. Off ⇒ agenda-only page. */
  showMonthStrip: z.boolean().default(ICAL_FEED_SHOW_MONTH_STRIP_DEFAULT),
  /** The category filter control in the strip. Off ⇒ no filter affordance. */
  showFilters: z.boolean().default(ICAL_FEED_SHOW_FILTERS_DEFAULT),
  /**
   * The floating glass header overlaid on the page (hamburger-or-back, chat,
   * notification bell) plus the page title it carries. Absent ⇒ shown; see
   * `ICAL_FEED_SHOW_HEADER_DEFAULT`. `enabled: false` means NO BAR AT ALL,
   * title included — unlike tile screens, an iCal page puts its title in the
   * bar, and a title-less bar is not a state this switch can produce.
   */
  header: ScreenHeaderConfigSchema.optional()
}

export const IcalFeedSettingsSchema = z.object(IcalFeedSettingsShape)
export type IcalFeedSettings = z.infer<typeof IcalFeedSettingsSchema>

/**
 * ICAL_FEED page content — the whole screen is one calendar feed. A small
 * `.strip()` object like `FeedPageContentSchema` and `WebFeedPageContentSchema`,
 * not a Puck payload: unknown keys are dropped rather than persisted, with the
 * consequence page content generally carries — a field added here is silently
 * dropped until the backend ships a rebuilt shared-types package.
 *
 * `feedUrl` accepts the empty string for the same load-bearing reason it does
 * on the web-feed shape: the page exists before its address does, and the
 * editor autosaves the whole payload. An empty url reaches the read path as
 * "no feed configured" and answers zero events.
 *
 * `lastProbedFeedUrl` is SERVER-OWNED: the write path records the url that last
 * passed an author-time reachability probe and ignores whatever the request
 * carried, so an author cannot skip their own probe by sending a value that
 * matches the url they are setting.
 *
 * ⚠️ The `.strip()` is why a new field here is a DEPLOY-ORDER problem, not just
 * a schema edit: a key the deployed backend's copy of this schema does not know
 * is dropped on save, silently and with a 200. The backend must ship a rebuilt
 * shared-types package BEFORE the editor starts writing a new setting, or an
 * author toggles a switch that never persists and nothing reports it.
 */
export const IcalFeedPageContentSchema = z
  .object({
    ...WebFeedAddressShape,
    ...IcalFeedSettingsShape,
    // Overrides the address shape's own `feedUrl` so a pasted `webcal://` link
    // is normalized before the https check (SCH-1506). Everything else about
    // the addressing — the feed id, the server-owned probe record — is the
    // shared one.
    feedUrl: IcalFeedUrlDraftSchema
  })
  .strip()
export type IcalFeedPageContent = z.infer<typeof IcalFeedPageContentSchema>
